// Shape primitives for the Avio ceļojumi background decor layer (composed by AvioDecorLayer in
// AvioCeljojumiCarousel.jsx). Lavender-only palette — no blue here, the section's blue stays on
// the cards/CTAs only. Opacity is set ONCE per shape (on the wrapping element's CSS, in
// AvioCeljojumiCarousel.module.css) — never also on the SVG root or on individual paths, so
// nothing compounds into a near-invisible result.

// Two wide diagonal ribbons (bottom-left to top-right), thick enough to read clearly, routed so
// their strongest/widest parts fall in the section's own side margins and the padding below the
// card row — not hidden entirely behind the opaque cards.
export function CurvedBands({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1920 800"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false">
      <defs>
        <linearGradient id="avio-band-a" x1="0" y1="0.1" x2="1" y2="0.3">
          <stop offset="0" stopColor="#D6C1EC" stopOpacity="0.1" />
          <stop offset="0.45" stopColor="#D6C1EC" stopOpacity="0.4" />
          <stop offset="1" stopColor="#D6C1EC" stopOpacity="0.1" />
        </linearGradient>
        <linearGradient id="avio-band-b" x1="0" y1="0.4" x2="1" y2="0.05">
          <stop offset="0" stopColor="#CBB1E4" stopOpacity="0.1" />
          <stop offset="0.45" stopColor="#CBB1E4" stopOpacity="0.36" />
          <stop offset="1" stopColor="#CBB1E4" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      {/* Thick band mostly in the left margin + bottom padding, only a thin diagonal sliver
          crossing behind the card row itself. */}
      <path
        d="M -120 520 C 260 420, 520 620, 820 560 C 1150 500, 1400 680, 1900 640
           L 1900 780 C 1400 820, 1150 640, 820 700 C 520 760, 260 560, -120 660 Z"
        fill="url(#avio-band-a)"
      />
      {/* Thick band mostly in the top margin (above the cards) + right margin. */}
      <path
        d="M -120 260 C 300 60, 650 180, 1000 60 C 1350 -60, 1600 120, 1900 20
           L 1900 150 C 1600 250, 1350 70, 1000 190 C 650 310, 300 190, -120 390 Z"
        fill="url(#avio-band-b)"
      />
    </svg>
  );
}

// A compact TV tower (shaft + railed observation deck + mast/antenna) with one small rooftop —
// narrow enough that its main silhouette sits in the section's own left margin, in front of the
// first card, instead of mostly hidden behind it.
export function TvTowerSilhouette({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 150 240"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false">
      <g fill="currentColor">
        <polygon points="38,240 62,240 58,205 42,205" />
        <polygon points="42,205 58,205 54,160 46,160" />
        <rect x="47" y="95" width="6" height="65" />
        <ellipse cx="50" cy="90" rx="20" ry="12" />
        <rect x="48" y="45" width="4" height="45" />
        <polygon points="46,45 54,45 50,20" />
        <line x1="50" y1="20" x2="50" y2="6" stroke="currentColor" strokeWidth="2" />
        <rect x="90" y="195" width="30" height="45" />
        <polygon points="90,195 120,195 105,172" />
      </g>
      <ellipse cx="50" cy="90" rx="24" ry="14" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

// Big Ben (tower + pyramidal spire + clock) positioned toward the visible right margin, with a
// Ferris wheel whose circle deliberately runs past the viewBox's own right edge — cropped there,
// reading as "partially off the edge" rather than needing an extra negative CSS offset.
export function BigBenWheelSilhouette({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 180 300"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false">
      <g fill="currentColor">
        <rect x="40" y="235" width="20" height="65" />
        <rect x="64" y="210" width="16" height="90" />
        <rect x="95" y="170" width="26" height="130" />
        <polygon points="95,170 121,170 108,140" />
        <circle cx="108" cy="190" r="7" />
      </g>
      <g stroke="currentColor" strokeWidth="2.5" fill="none">
        <circle cx="150" cy="170" r="58" />
        <line x1="150" y1="112" x2="150" y2="228" />
        <line x1="92" y1="170" x2="208" y2="170" />
        <line x1="109" y1="129" x2="191" y2="211" />
        <line x1="191" y1="129" x2="109" y2="211" />
      </g>
      <g fill="currentColor">
        <circle cx="150" cy="112" r="4" />
        <circle cx="150" cy="228" r="4" />
        <circle cx="92" cy="170" r="4" />
        <circle cx="109" cy="129" r="4" />
        <circle cx="109" cy="211" r="4" />
      </g>
    </svg>
  );
}

// A small lavender plane silhouette + a dashed route behind it — compact, meant to sit fully
// within its box (see .flightPath for the exact position, chosen to clear the header row/
// "Skatīt visus" button horizontally with a wide margin).
export function FlightPathDecoration({ className }) {
  return (
    <div className={className}>
      <svg viewBox="0 0 260 110" width="100%" height="100%" aria-hidden="true" focusable="false">
        <path
          d="M8 95 C 70 80, 150 55, 235 18"
          stroke="#ae8bcd"
          strokeWidth="2"
          strokeDasharray="3 9"
          strokeLinecap="round"
          fill="none"
        />
        <path
          transform="translate(208 4) rotate(26) scale(1.2)"
          d="M0 11c0-1.8.9-2.9 2.6-2.9h9.3L20.7 0h3.9l-3.6 8.1h6.4c1.4 0 2.6 1.1 2.6 2.9s-1.2 2.9-2.6 2.9h-6.4l3.6 8.1h-3.9l-8.8-8.1H2.6C.9 13.9 0 12.8 0 11z"
          fill="#ae8bcd"
        />
      </svg>
    </div>
  );
}

// A small soft cloud shape (overlapping ellipses) — used in pairs above the card row.
export function CloudGroup({ className }) {
  return (
    <svg className={className} viewBox="0 0 120 50" aria-hidden="true" focusable="false">
      <g fill="#e2d5f1">
        <ellipse cx="38" cy="30" rx="30" ry="14" />
        <ellipse cx="68" cy="24" rx="20" ry="11" />
        <ellipse cx="18" cy="26" rx="14" ry="9" />
      </g>
    </svg>
  );
}
